import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { Link } from '../../../../front/src/components/Link';
import { useElementSize } from '../../../../front/src/hooks/useElementSize';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { getHeadingIcon } from '../../../../front/src/icons/headingSets/corevibe';
import { useAppSelector } from '../../../../front/src/redux/hooks';
import { selectHeadings } from '../../../../front/src/thread/ThreadMessage/topics-slice';
import type { QueryParamsMapType } from '../../../../front/src/utils/routing';
import { emojiPrefixedParser } from '../../../../front/src/utils/strings';
import { CircularPicker } from '../../utils/WebsiteColorsPicker';
import { headingsMessages } from '../Layout/BlockFeatured';
import { fixedTwBgColors } from '../Layout/BlockSidebarFeatured';

// Same fallback palette as the vertical NewHeadingsMenu so colors stay consistent.
const FixedColorsPicker = new CircularPicker(fixedTwBgColors);
const isTwBgClass = (color?: string | null): boolean => !!color && color.startsWith('bg');

// Shared header background: the tenant theme sets `--gc-header-bg` (via a custom `head` CSS
// block, like `--color-primary`); otherwise it falls back to the default gradient. The navbar
// (Header/index.tsx) uses the SAME value so, when the menu is sticky, the two bars read as one
// seamless bar (see B3_02).
const HEADER_BG = 'var(--gc-header-bg, linear-gradient(to right, #F2F9FB, #F3F2FB))';

const chipClassName =
  'HeadingsNavBar_chip inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-white/70 px-3 py-1.5 text-[14px] font-semibold shadow-sm transition-colors hover:bg-white hover:text-primary';

/** A rendered chip: label + dot/icon + the route it links to. */
interface Chip {
  uid?: number | null;
  label: string;
  emoji?: string | null;
  color?: string | null;
  icon?: string | null;
  /** Link route key (gdRoutes) + its params, precomputed per mode. */
  to: string;
  params: QueryParamsMapType;
}

interface HeadingsNavBarProps {
  /** Chip dot color source: 'heading' = the rubrique's own color, 'palette' = cyclic fallback. */
  colorMode?: 'heading' | 'palette';
  /** Append a "See all tags" chip linking to the all-tags page. Forum only (no-op in media). */
  showAllLink?: boolean;
  /**
   * When true (block option `sticky`), the bar glues right under the navbar on scroll (like the
   * search bar) — offset = navbar height + `stickyTopOffset`. When false, it scrolls away with
   * the page. Either way it keeps its own background (the shared `--gc-header-bg` token), so a
   * sticky bar merges seamlessly with the sticky navbar above it. B3_02.
   */
  sticky?: boolean;
  /**
   * Extra top offset above the navbar, forwarded from MainLayout: the height of any sticky
   * header-zone block rendered above the navbar (`dynamicStickyHeaderHeight`). The bar sticks
   * below the navbar, so its own offset = this + the navbar height (read live).
   */
  stickyTopOffset?: number;
}

/**
 * Horizontal, magazine-style top navigation bar.
 * Rendered by MainLayout at the head of MAIN (full-width, nothing above it) when an active
 * `headings-nav` layout block exists (BO → Mise en forme). Block options drive `colorMode`,
 * `showAllLink` and `sticky` (glue under the navbar on scroll).
 *
 * - **Media mode** (`graphdebate.mode === 'media'`): the bar becomes the editorial showcase —
 *   chips are the `editorialMenu` entries (B4_01 contract C6) and link to the theme/entity facet
 *   pages (`/theme/<slug>`, `/entity/<slug>`), never to a `category` tag. When the editorial menu
 *   is empty (tenant not seeded yet), the bar is hidden (rubriques live in the B4_03 forum block).
 * - **Forum mode / default**: unchanged — chips are the rubriques (`selectHeadings`), each linking
 *   to its first child category, falling back to the tags page. Zero regression on existing forums.
 */
const HeadingsNavBar: FC<HeadingsNavBarProps> = memo(function HeadingsNavBar({
  colorMode = 'heading',
  showAllLink = false,
  sticky = false,
  stickyTopOffset = 0,
}) {
  const { graphdebate } = useWebsite();
  const isMedia = graphdebate?.mode === 'media';
  const editorialMenu = graphdebate?.editorialMenu;
  const headings = useAppSelector(selectHeadings);

  // When sticky, glue right under the navbar: offset = navbar height (read live) + any sticky
  // header-zone block above it (stickyTopOffset). Reuses the same NavbarHeader measurement as
  // the sidebar sticky blocks (AreaBlocks).
  const { height: navbarHeight } = useElementSize('NavbarHeader');

  const chips: Chip[] | undefined = useMemo(() => {
    // Media: editorial menu → theme/entity facet pages. `target === null` entries (deleted /
    // unapproved / wrong-type tag, per C6 read guard) are dropped.
    if (isMedia) {
      return editorialMenu
        ?.filter(entry => !!entry?.target?.slug)
        .map(entry => ({
          uid: entry?.uid,
          label: entry?.title || entry?.target?.title || '',
          color: colorMode === 'palette' ? FixedColorsPicker.pick() : entry?.color,
          icon: entry?.icon,
          to: entry?.target?.type === 'theme' ? 'theme' : 'entity',
          // slug is guaranteed non-empty by the filter above; `?? ''` only satisfies the type.
          params: { slug: entry?.target?.slug ?? '' },
        }));
    }

    // Forum / default: rubriques → first child category, falling back to the tags page.
    return headings?.map(heading => {
      const { emoji, content } = emojiPrefixedParser(heading?.content || '');
      const tagSlug = heading?.children?.[0]?.slug;
      const params: QueryParamsMapType = tagSlug ? { tag: tagSlug } : {};
      return {
        uid: heading?.uid,
        label: content || heading?.content || '',
        emoji,
        color:
          colorMode === 'palette'
            ? FixedColorsPicker.pick()
            : heading?.color || FixedColorsPicker.pick(),
        icon: heading?.icon,
        to: tagSlug ? 'category' : 'tags',
        params,
      };
    });
  }, [isMedia, editorialMenu, headings, colorMode]);

  if (!chips?.length) return null;

  return (
    <nav
      className={twMerge(
        'HeadingsNavBar w-full border-b border-black/5',
        // z-[350]: above the EditorJS toolbar (.ce-toolbar, z-[300]) and page content so the bar
        // stays on top when sticky, but BELOW the navbar (z-[400]) so the navbar's search dropdown
        // — which expands downward over this bar — is never hidden behind it. Same background as
        // the navbar → seamless merge when both are sticky.
        sticky && 'sticky z-[350]',
      )}
      style={{
        backgroundImage: HEADER_BG,
        ...(sticky && { top: navbarHeight + stickyTopOffset }),
      }}
    >
      <div className="HeadingsNavBar_content mx-auto flex w-full max-w-[1024px] items-center gap-2 overflow-x-auto px-[18px] pb-2.5 pt-2.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {chips.map((chip, index) => {
          const Icon = getHeadingIcon(chip.icon);
          const inner = (
            <a className={chipClassName}>
              {Icon ? (
                <Icon className="h-[18px] w-[18px] shrink-0" />
              ) : chip.emoji ? (
                <span className="text-[15px] leading-none">{chip.emoji}</span>
              ) : (
                <span
                  className={twMerge(
                    'h-2 w-2 shrink-0 rounded-full',
                    isTwBgClass(chip.color) && chip.color,
                  )}
                  {...(!isTwBgClass(chip.color) && {
                    style: { backgroundColor: chip.color ?? undefined },
                  })}
                />
              )}
              <span>{chip.label}</span>
            </a>
          );

          return (
            <Link key={chip.uid ?? index} to={chip.to} params={chip.params}>
              {inner}
            </Link>
          );
        })}
        {/* "See all tags" is a forum notion (links to /tags): hidden in media mode. */}
        {showAllLink && !isMedia && (
          <Link key="all-tags" to="tags" params={{}}>
            <a className={chipClassName}>
              <span>
                <FormattedMessage {...headingsMessages.allCategories} />
              </span>
            </a>
          </Link>
        )}
      </div>
    </nav>
  );
});

export default HeadingsNavBar;
